import { html, SP } from '../../html.js';
import type { VNode } from '../../html.js';
import type { DashboardActions, DashboardState } from '../store.js';
import { ErrorNote } from './bits.js';

/**
 * The login screen, with the operator path folded underneath it.
 *
 * Sign-in is a link sent by email and nothing else: no password field exists
 * on this screen or anywhere behind it, and the copy never says "password"
 * because the first thing a person does with a login form is reach for one.
 *
 * "Check your inbox" is deliberately the same sentence for an address that has
 * an account and one that does not. `POST /api/auth/login` answers 202 for any
 * well-formed address so nobody can enumerate customers through it, and a
 * screen that said "we found you" would hand that back.
 *
 * `devLink` exists only under `CLAWHARBOR_EMAIL_PROVIDER=log`, the mode a host
 * runs in before a provider key exists. It is shown plainly, labelled as the
 * link that would have been emailed, because it is how the operator proves the
 * whole loop works — request, callback, cookie, dashboard — before spending a
 * cent on email. A real provider never puts it in the body.
 *
 * The operator section is collapsed under the form rather than removed. It is
 * still the only authority on a host with no accounts routes, and it still
 * governs every tenant on the box, so the warning about scope stays on the one
 * screen where the secret is in somebody's hand.
 */
export function LoginScreen(props: { state: DashboardState; actions: DashboardActions }): VNode {
  const { state, actions } = props;

  const form = state.sent
    ? html`
        <div class="login__sent" role="status">
          <p class="login__sent-h">Check your inbox.</p>
          <p class="login__sent-p">
            If <b>${state.email}</b> can sign in here, a link is on its way to it. The link works
            once and for a short while; nothing happens until you open it.
          </p>
          ${state.devLink
            ? html`<div class="login__dev">
                <p class="login__dev-tag">
                  Development mode — this link would have been emailed
                </p>
                <p class="login__dev-p">
                  This host runs with <code>CLAWHARBOR_EMAIL_PROVIDER=log</code>, so no message was
                  sent. Open the link to finish signing in exactly as the email would.
                </p>
                <a class="login__dev-link" href=${state.devLink}>${state.devLink}</a>
              </div>`
            : null}
          <button type="button" class="linkish" onClick=${() => actions.resetLogin()}>
            Use a different address
          </button>
        </div>
      `
    : html`
        <form
          class="login__form"
          onSubmit=${(e: Event) => {
            e.preventDefault();
            actions.sendLink();
          }}
        >
          <label class="unlock__label" for="login-email">Email</label>
          <input
            id="login-email"
            class="unlock__input"
            type="email"
            name="email"
            autocomplete="email"
            inputmode="email"
            spellcheck="false"
            autocapitalize="off"
            required
            placeholder="you@example.com"
            value=${state.email}
            onInput=${(e: Event) => actions.setEmail((e.target as HTMLInputElement).value)}
          />
          <button type="submit" class="btn" disabled=${!state.email.trim() || state.sending}>
            ${state.sending ? 'Sending…' : 'Send me a link'}
          </button>
        </form>
        ${ErrorNote({ message: state.loginError })}
      `;

  return html`
    <section class="wrap unlock">
      <p class="eyebrow">Dashboard</p>
      <h1 class="unlock__h" tabindex="-1" data-focus-target>Sign in</h1>
      <p class="unlock__p">
        Enter the address you published with and we send a link that signs you in. There is no
        password: the link is the whole credential, it works once, and it expires. An address that
        is new here gets its account the moment the link is opened, and nothing is created before
        that.
      </p>
      <ul class="unlock__terms">
        <li><b>Sites</b> — every site you own: its health, what agents called, and the unpublish switch.</li>
        <li><b>Add a site</b> — start a crawl from here and keep the job attached to your account.</li>
        <li><b>Access</b> — on each site, the addresses that may operate it, added and removed by you.</li>
      </ul>

      ${state.loginNotice ? html`<p class="notice login__notice" role="status">${state.loginNotice}</p>` : null}

      ${form}

      <details class="operator" open=${state.secret.length > 0 || state.unlockError != null}>
        <summary class="devs__summary">I am the operator of this host</summary>
        <div class="operator__body">
          <p class="operator__p">
            The operator holds <code>CLAWHARBOR_RELOAD_SECRET</code> — the value this server process
            was started with — and that one value governs${SP}
            <b>every tenant published on this host</b>: their manifests, their tool descriptions and
            the unpublish switch. It is not an account and it does not sign anybody in.
          </p>
          <form
            class="unlock__form"
            onSubmit=${(e: Event) => {
              e.preventDefault();
              actions.unlock();
            }}
          >
            <label class="unlock__label" for="operator-secret">Reload secret</label>
            <input
              id="operator-secret"
              class="unlock__input"
              type="password"
              autocomplete="off"
              spellcheck="false"
              autocapitalize="off"
              placeholder="CLAWHARBOR_RELOAD_SECRET"
              value=${state.secret}
              onInput=${(e: Event) => actions.setSecret((e.target as HTMLInputElement).value)}
            />
            <button type="submit" class="btn btn--ghost" disabled=${!state.secret || state.unlocking}>
              ${state.unlocking ? 'Checking…' : 'Unlock as operator'}
            </button>
          </form>
          ${ErrorNote({ message: state.unlockError })}
          <ul class="unlock__terms">
            <li>
              Held in memory for this page only. It is never written to${SP}
              <code>localStorage</code>, <code>sessionStorage</code>, a cookie, or a URL — a URL ends
              up in browser history, in a proxy log and in a screenshot, and there is no per-tenant
              scoping behind this secret to limit what that would cost.
            </li>
            <li>Sent as a request header on every call this page makes, and on nothing else.</li>
            <li>
              Closing the tab forgets it. "Forget secret" in the rail does the same thing without
              closing the page.
            </li>
          </ul>
        </div>
      </details>
    </section>
  `;
}
